// 동료 기본 클래스 - AI 동작 로직을 포함한 추상 클래스 (Party 상속)
class Companion extends Party {
  constructor(scene, player, config, type) {
    // Party 생성자 호출
    super(scene, 0, 0, 0, config);

    // 동료 전용 속성들
    this.player = player;
    this.type = type; // ROGUE, BARBARIAN, MAGE 등

    // Companion AI 전용 속성 (Player는 사용하지 않음)
    this.targetEnemy = null; // 현재 타겟팅 중인 적
    this.followDistance = config.FOLLOW_DISTANCE; // 플레이어 추적 거리
    this.attackDistance = config.ATTACK_RANGE; // 적 감지 거리
    this.companions = []; // 다른 동료들 참조 (마법사의 힐 등에 사용)

    // Companion 상태 설정 (Entity의 currentState 오버라이드)
    this.currentState = "IDLE"; // FOLLOW, ATTACK, IDLE

    // Companion 전용 애니메이션: sit 애니메이션 (휴식 시)
    this.sitAction = null;

    this.init();
  }

  // 초기화 - 부모 클래스 메서드 오버라이드
  async init() {
    await this.loadModel();
    this.createHealthBar();
    // 애니메이션이 이미 설정되어 있으면 건너뛰기
    if (!this.animations || Object.keys(this.animations).length === 0) {
      this.setupAnimations();
    }

    // 부채꼴 공격 범위 시각화 생성
    this.createAttackVisual();

    // 초기에는 부채꼴 숨김 (AIM/ATTACK 상태에서만 표시)
    if (this.attackVisualMesh) {
      this.attackVisualMesh.visible = false;
    }

    this.isLoaded = true;
  }

  // 모델 로드 - 캐시된 모델 사용 (기본 구현)
  async loadModel() {
    try {
      // 캐시된 모델 사용
      const modelData = await window.companionModelCache.createCompanionModel(
        this.type
      );

      this.model = modelData.scene;

      // 모델 속성 설정 (그림자, 스케일, 위치 등)
      this.setupModelProperties();

      // 서브클래스별 무기 숨기기 처리
      this.hideWeapons();

      this.scene.add(this.model);

      // 애니메이션 설정 (부모 클래스 메서드 사용)
      const animations = modelData.animations || [];
      if (animations.length > 0) {
        // 부모 클래스의 setupAnimations 호출 (공통 애니메이션 설정)
        this.setupAnimations(animations);
      }
    } catch (error) {}
  }

  // 무기 숨기기 - 서브클래스에서 오버라이드
  hideWeapons() {
    // 기본 구현은 아무것도 하지 않음 (서브클래스에서 필요시 오버라이드)
  }

  // 애니메이션 인덱스 정의 (부모 클래스 메서드 오버라이드)
  // 서브클래스(RogueCompanion, MageCompanion 등)에서 다시 오버라이드 가능
  getAnimationIndices() {
    throw new Error("getAnimationIndices 메서드를 구현해야 합니다.");
  }

  // 애니메이션 설정 - 부모 클래스 메서드 오버라이드
  // 서브클래스의 loadModel에서 애니메이션 배열을 전달해야 함
  setupAnimations(animations) {
    // 부모 클래스의 setupAnimations 호출 (공통 애니메이션 설정)
    super.setupAnimations(animations);

    // Companion 전용: 앉기 애니메이션 설정
    if (animations[56]) {
      this.sitAction = this.mixer.clipAction(animations[56]);
      this.sitAction.setLoop(THREE.LoopOnce);
      this.sitAction.clampWhenFinished = true;
    }

    // allAnimations 배열에 추가 앉기 애니메이션 추가
    if (this.sitAction) {
      this.allAnimations.push(this.sitAction);
    }
  }

  // 업데이트 - 매 프레임 호출 (부모 클래스 메서드 오버라이드)
  update(deltaTime, enemies, companions = [], camera = null) {
    // 한국어 주석: 죽음 애니메이션 재생을 위해 항상 부모 클래스 업데이트 호출 (mixer 업데이트 필요)
    super.update(deltaTime);

    // 살아있을 때만 AI 및 기타 로직 실행
    if (!this.isAlive) {
      // 데미지 숫자는 죽을 때도 업데이트 (표시되던 숫자들 정리)
      this.updateDamageNumbers(deltaTime);
      return;
    }

    // 동료들 참조 저장 (마법사의 힐 등에 사용)
    this.companions = companions;

    // AI 상태 머신 업데이트
    this.updateAI(deltaTime, enemies);

    // 위치 업데이트
    this.updatePosition();

    // 부채꼴 시각화 업데이트 (AIM이나 ATTACK 상태일 때만 표시)
    this.updateAttackVisualization();

    // 체력바 업데이트
    this.updateHealthBar(camera);

    // 데미지 숫자 업데이트
    this.updateDamageNumbers(deltaTime);
  }

  // AI 상태 머신
  updateAI(deltaTime, enemies) {
    // 강제 idle 상태면 AI 업데이트 하지 않음 (게임오버 시 앉기 애니메이션 유지)
    if (this.isForcedIdle) return;

    const playerDistance = this.getDistanceToPlayer();
    const nearestEnemy = this.findNearestEnemy(enemies);
    const enemyDistance = nearestEnemy
      ? this.getDistanceToEnemy(nearestEnemy)
      : Infinity;
    const aimDistance = this.attackDistance * 1.2;

    // 상태별 핸들러 실행
    const context = {
      nearestEnemy,
      enemyDistance,
      playerDistance,
      aimDistance,
      deltaTime,
    };
    const handlers = {
      FOLLOW: this.handleFollowState.bind(this),
      AIM: this.handleAimState.bind(this),
      ATTACK: this.handleAttackState.bind(this),
      IDLE: this.handleIdleState.bind(this),
    };

    const handler = handlers[this.currentState];
    if (handler) handler(context);
  }

  // FOLLOW 상태 처리
  handleFollowState({
    nearestEnemy,
    enemyDistance,
    playerDistance,
    aimDistance,
  }) {
    if (nearestEnemy && enemyDistance <= this.attackDistance) {
      this.transitionToAttack(nearestEnemy);
    } else if (nearestEnemy && enemyDistance <= aimDistance) {
      this.transitionToAim(nearestEnemy);
    } else if (playerDistance > this.followDistance) {
      this.moveTowardsPlayer();
      this.playAnimation("run");
    } else {
      this.transitionToIdle();
    }
  }

  // AIM 상태 처리
  handleAimState({ nearestEnemy, enemyDistance, aimDistance }) {
    if (!nearestEnemy || enemyDistance > aimDistance) {
      this.transitionToFollow();
    } else if (enemyDistance <= this.attackDistance) {
      this.transitionToAttack(nearestEnemy);
    } else {
      this.lookAtEnemy(nearestEnemy);
      this.playAnimation("aim");
    }
  }

  // ATTACK 상태 처리
  handleAttackState({ nearestEnemy, enemyDistance, aimDistance, deltaTime }) {
    // 적이 없으면 FOLLOW로 전환
    if (!nearestEnemy) {
      this.transitionToFollow();
      return;
    }

    // 적이 공격 범위를 벗어났으면 AIM으로 전환 (자연스러운 전환)
    if (enemyDistance > this.attackDistance) {
      // AIM 범위 내에 있으면 AIM 상태로
      if (enemyDistance <= aimDistance) {
        this.transitionToAim(nearestEnemy);
      } else {
        // AIM 범위도 벗어났으면 FOLLOW로
        this.transitionToFollow();
      }
      return;
    }

    // 공격 범위 내에 있으면 공격 수행
    this.lookAtEnemy(nearestEnemy);
    this.attackEnemy(nearestEnemy, deltaTime);
    this.playAnimation("attack");
  }

  // IDLE 상태 처리
  handleIdleState({
    nearestEnemy,
    enemyDistance,
    playerDistance,
    aimDistance,
  }) {
    if (nearestEnemy && enemyDistance <= this.attackDistance) {
      this.transitionToAttack(nearestEnemy);
    } else if (nearestEnemy && enemyDistance <= aimDistance) {
      this.transitionToAim(nearestEnemy);
    } else if (playerDistance > this.followDistance * 1.5) {
      this.currentState = "FOLLOW";
    } else {
      // IDLE 상태일 때 플레이어를 바라봄
      this.lookAtPlayer();
      this.playAnimation("idle");
    }
  }

  // 상태 전환 헬퍼 메서드들
  transitionToAttack(enemy) {
    this.currentState = "ATTACK";
    this.targetEnemy = enemy;
    this.playAnimation("attack");
  }

  transitionToAim(enemy) {
    this.currentState = "AIM";
    this.targetEnemy = enemy;
    this.playAnimation("aim");
  }

  transitionToFollow() {
    this.currentState = "FOLLOW";
    this.targetEnemy = null;
    this.playAnimation("idle");
  }

  transitionToIdle() {
    this.currentState = "IDLE";
    this.playAnimation("idle");
  }

  // 부채꼴 시각화 업데이트 오버라이드 (AIM, ATTACK 상태에서만 표시)
  updateAttackVisualization() {
    if (!this.showAttackVisual || !this.attackVisualMesh) return;

    // AIM이나 ATTACK 상태일 때만 부채꼴 표시
    const shouldShow =
      this.currentState === "AIM" || this.currentState === "ATTACK";
    this.attackVisualMesh.visible = shouldShow;

    // 보이는 상태일 때만 위치와 색상 업데이트
    if (shouldShow) {
      // 위치와 회전에 따라 부채꼴 위치 업데이트
      this.updateAttackVisualPosition();

      // 부채꼴 범위 내 적 확인하여 색상 변경
      this.updateAttackVisualColor();
    }
  }

  // 가장 가까운 적 찾기
  findNearestEnemy(enemies) {
    if (!enemies || enemies.length === 0) return null;

    let nearestEnemy = null;
    let nearestDistance = Infinity;

    enemies.forEach((enemy) => {
      if (enemy.isAlive) {
        const distance = this.getDistanceToEnemy(enemy);
        if (distance < nearestDistance) {
          nearestDistance = distance;
          nearestEnemy = enemy;
        }
      }
    });

    return nearestEnemy;
  }

  // 적을 향해 회전
  lookAtEnemy(enemy) {
    if (!enemy) return;

    const direction = new THREE.Vector3()
      .subVectors(enemy.position, this.position)
      .normalize();

    this.lookAtDirection(direction);
  }

  // 플레이어를 향해 회전
  lookAtPlayer() {
    if (!this.player) return;

    const direction = new THREE.Vector3()
      .subVectors(this.player.position, this.position)
      .normalize();

    this.lookAtDirection(direction);
  }

  // 플레이어까지의 거리 계산
  getDistanceToPlayer() {
    return this.getDistanceToEntity(this.player);
  }

  // 적까지의 거리 계산
  getDistanceToEnemy(enemy) {
    return this.getDistanceToEntity(enemy);
  }

  // 플레이어를 향해 이동
  moveTowardsPlayer() {
    const distance = this.getDistanceToEntity(this.player);

    // 플레이어와의 최소 거리 유지 (겹침 방지)
    const minDistance = this.colliderRadius + this.player.colliderRadius + 0.2;

    if (distance > minDistance) {
      // 방향 벡터 계산
      const direction = new THREE.Vector3()
        .subVectors(this.player.position, this.position)
        .normalize();
      // 플레이어 방향으로 회전
      this.lookAtDirection(direction);

      // 새로운 위치 계산 (멤버 변수 사용)
      const moveVector = direction.multiplyScalar(this.moveSpeed);
      const newPosition = new THREE.Vector3()
        .copy(this.position)
        .add(moveVector);

      // 위치 업데이트
      this.updatePosition(newPosition);
    }
  }

  // 적 공격 - 부채꼴 범위 내 모든 적 공격
  attackEnemy(enemy, deltaTime) {
    const currentTime = Date.now() / 1000;

    // 적을 향해 회전 (가장 가까운 적 방향으로)
    this.lookAtEnemy(enemy);

    // 부채꼴 범위의 모든 적 공격 (Party의 메서드 사용)
    this.attackEnemiesInSector(currentTime);
  }

  // 실제 공격 수행 - 이제 사용하지 않음 (부채꼴 공격으로 대체)
  // performAttack 메서드는 제거됨 (attackEnemiesInSector가 대체)

  // 피격 처리 - 부모 클래스 메서드 오버라이드
  takeDamage(damage, attackerPosition = null) {
    const result = super.takeDamage(damage, attackerPosition);

    // 데미지 숫자 표시 (Enemy와 동일한 방식)
    if (this.model && this.health > 0) {
      this.showDamageNumber(damage);
    }

    if (result) {
      this.die();
    }
    return result;
  }

  // 죽음 처리 - 부모 클래스 메서드 오버라이드
  die() {
    // 이미 죽었다면 중복 호출 방지 (중복 로그 방지)
    if (this.currentState === "DEAD") {
      return;
    }

    this.currentState = "DEAD";

    // 부모 클래스의 die 메서드 호출 (체력바 제거 포함, 죽음 애니메이션 재생)
    super.die();

    // 데미지 숫자들 정리
    this.clearDamageNumbers();

    // 한국어 주석: 동료 사망 로그는 CompanionManager의 removeCompanion에서 표시함 (중복 방지 및 정확한 카운트를 위해)
    // 한국어 주석: 모델은 즉시 제거하지 않고, Entity의 updateDeathState에서 죽음 애니메이션이 끝난 후 제거됨
  }

  // 애니메이션 재생 - 부모 클래스 메서드 오버라이드
  // aim 애니메이션이 없으면 attack 애니메이션을 재생
  playAnimation(animationName) {
    // aim 애니메이션 요청인데 aimAction이 없으면 attack으로 대체
    if (animationName === "aim" && !this.aimAction) {
      animationName = "attack";
    }

    // 부모 클래스의 playAnimation 호출
    super.playAnimation(animationName);
  }

  // 앉기 애니메이션 재생 (게임오버 시 사용)
  playSitAnimation() {
    if (!this.sitAction || !this.isAlive || this.isDying) return;

    // AI 업데이트를 중단하기 위해 강제 idle 플래그 설정
    this.isForcedIdle = true;
    this.currentState = "SIT";

    // 앉기 애니메이션으로 전환
    this.switchAnimation(this.currentAnimation, this.sitAction, 0.4);
  }
}

// 전역으로 내보내기
if (typeof module !== "undefined" && module.exports) {
  module.exports = Companion;
} else {
  window.Companion = Companion;
}

// webpack 빌드를 위해 default export 추가
export default Companion;
